import type { AdapterCallState, SoftphoneAdapter, SoftphoneAdapterEvent, SoftphoneAdapterListener } from './adapter.js';

// SoftphoneAdapter over Telnyx's WebRTC SDK (`@telnyx/webrtc`, pinned 2.27.10), loaded lazily so the SDK is only
// downloaded when an advisor connects the softphone.
//
// SDK assumptions — «sin verificar» (read from the package's type declarations and Telnyx docs, never exercised against
// a real Telnyx account from this code):
//  • `new TelnyxRTC({ login_token })` authenticates with the short-lived login token LIA mints; `client.connect()`
//    opens the socket; `client.remoteElement` takes the <audio> element that plays the remote party;
//  • events: 'telnyx.ready' (registered), 'telnyx.error', 'telnyx.socket.close', 'telnyx.notification' with
//    `{ type: 'callUpdate', call }`;
//  • `call.state` strings: 'new' | 'requesting' | 'trying' | 'ringing' | 'answering' | 'early' | 'active' | 'held' |
//    'hangup' | 'destroy' | 'purge'; an inbound invite arrives as 'ringing' with `call.direction === 'inbound'`;
//  • the caller number of an invite is in `call.options.remoteCallerNumber` (fallback `callerNumber`);
//  • `call.answer()`, `call.hangup()`, `call.muteAudio()` / `unmuteAudio()`, `call.hold()` / `unhold()`, `call.dtmf(d)`.
// The login token is only passed to the SDK: it is never stored or logged here.

interface TelnyxCallLike {
  id: string;
  state: string;
  direction?: string;
  options?: { remoteCallerNumber?: string; callerNumber?: string };
  answer(params?: unknown): Promise<void> | void;
  hangup(): Promise<void> | void;
  muteAudio(): void;
  unmuteAudio(): void;
  hold(): Promise<unknown> | void;
  unhold(): Promise<unknown> | void;
  dtmf(digit: string): void;
}

interface TelnyxClientLike {
  remoteElement?: unknown;
  on(event: string, cb: (payload: unknown) => void): unknown;
  off?(event: string, cb?: (payload: unknown) => void): unknown;
  connect(): Promise<void> | void;
  disconnect(): Promise<void> | void;
}

export interface TelnyxSdkModule {
  TelnyxRTC: new (options: { login_token: string }) => TelnyxClientLike;
}

export interface TelnyxAdapterOptions {
  /** Loads the SDK module (injectable for tests). */
  loadSdk?: () => Promise<TelnyxSdkModule>;
  /** Element that plays the remote audio; one is created (hidden, autoplay) when omitted. */
  audioElement?: HTMLAudioElement;
}

const defaultLoad = async (): Promise<TelnyxSdkModule> => (await import('@telnyx/webrtc')) as unknown as TelnyxSdkModule;

function toAdapterState(state: string): AdapterCallState | null {
  if (state === 'ringing') return 'ringing';
  if (state === 'active') return 'active';
  if (state === 'held') return 'held';
  if (state === 'hangup' || state === 'destroy' || state === 'purge') return 'hangup';
  return null;
}

export function createTelnyxAdapter(opts: TelnyxAdapterOptions = {}): SoftphoneAdapter {
  const listeners = new Set<SoftphoneAdapterListener>();
  const emit = (e: SoftphoneAdapterEvent) => listeners.forEach((l) => l(e));
  let client: TelnyxClientLike | null = null;
  let call: TelnyxCallLike | null = null;
  let audio: HTMLAudioElement | null = opts.audioElement ?? null;
  let createdAudio = false;
  const lastState = new Map<string, AdapterCallState>();

  const ensureAudio = (): HTMLAudioElement | null => {
    if (audio || typeof document === 'undefined') return audio;
    audio = document.createElement('audio');
    audio.autoplay = true;
    audio.hidden = true;
    audio.setAttribute('data-softphone-audio', '');
    document.body.appendChild(audio);
    createdAudio = true;
    return audio;
  };

  const onNotification = (payload: unknown) => {
    const n = payload as { type?: string; call?: TelnyxCallLike } | null;
    if (!n || n.type !== 'callUpdate' || !n.call) return;
    const c = n.call;
    const st = toAdapterState(String(c.state));
    if (!st || lastState.get(c.id) === st) return;
    lastState.set(c.id, st);
    if (st === 'hangup') {
      if (call?.id === c.id) call = null;
      emit({ type: 'call', callId: c.id, state: 'hangup' });
      return;
    }
    call = c;
    if (st === 'ringing' && c.direction === 'inbound') {
      emit({ type: 'incoming', callId: c.id, remoteNumber: c.options?.remoteCallerNumber ?? c.options?.callerNumber ?? null });
      return;
    }
    emit({ type: 'call', callId: c.id, state: st });
  };
  const onReady = () => emit({ type: 'ready' });
  const onError = () => emit({ type: 'error', message: 'El softphone tuvo un error de conexión con Telnyx.' });
  const onClose = () => emit({ type: 'disconnected' });

  const detach = (c: TelnyxClientLike) => {
    c.off?.('telnyx.ready', onReady);
    c.off?.('telnyx.error', onError);
    c.off?.('telnyx.socket.close', onClose);
    c.off?.('telnyx.notification', onNotification);
  };

  return {
    async connect(loginToken) {
      if (client) {
        detach(client);
        await Promise.resolve(client.disconnect()).catch(() => undefined);
        client = null;
      }
      const sdk = await (opts.loadSdk ?? defaultLoad)();
      const c = new sdk.TelnyxRTC({ login_token: loginToken });
      const el = ensureAudio();
      if (el) c.remoteElement = el;
      c.on('telnyx.ready', onReady);
      c.on('telnyx.error', onError);
      c.on('telnyx.socket.close', onClose);
      c.on('telnyx.notification', onNotification);
      client = c;
      await c.connect();
    },
    async disconnect() {
      const c = client;
      client = null;
      call = null;
      lastState.clear();
      if (c) {
        detach(c);
        await Promise.resolve(c.disconnect()).catch(() => undefined);
      }
      if (createdAudio && audio) {
        audio.remove();
        audio = null;
        createdAudio = false;
      }
    },
    async answer() {
      if (call) await call.answer();
    },
    async hangup() {
      if (call) await call.hangup();
    },
    setMuted(muted) {
      if (!call) return;
      if (muted) call.muteAudio();
      else call.unmuteAudio();
    },
    async setHeld(held) {
      if (!call) return;
      await (held ? call.hold() : call.unhold());
    },
    sendDtmf(digit) {
      call?.dtmf(digit);
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
